import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { Col, Row, useRequireAuth, LoginForm } from 'components';
import { IScreen } from 'type';
import { useNavFunc } from 'navigation';
import { SCREEN } from 'const';
import Header from './components/Header';
import SummaryMetrics from './components/SummaryMetrics';
import DeliveryFeed from './components/DeliveryFeed';
import KPIPanel from './components/KPIPanel';
import IssuesPanel from './components/IssuesPanel';
import LiveTicker from './components/LiveTicker';
import { DeliveryCardData } from './components/DeliveryCard';
import { IssueCardData } from './components/IssueCard';
import { useDashboardData, useDashboardStore } from 'store/Dashboard.Store';
import moment from 'moment';
import { TimeHelper } from 'helpers';
import Store from 'store';

const LIGHT_GREY = '#F5F6F7';

const OperationsDashboard: IScreen = () => {
  const { isLoggedIn } = useRequireAuth();
  const { navigate } = useNavFunc();
  const [liveClock, setLiveClock] = useState('');
  const [lastUpdate, setLastUpdate] = useState('2s ago');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { height: windowHeight } = useWindowDimensions();
  const lastUpdateTimeRef = useRef<Date | null>(null);
  const containerRef = useRef<any>(null);
  const tokenProcessedRef = useRef<boolean>(false);
  const UserStore = Store.useUserStore();
  const [hasTokenInUrl, setHasTokenInUrl] = useState(false);

  const { uiState } = useDashboardData();
  const { orders, issues, totalOrdersToday, snapshots, totalOpenIssues, issuesByDriver, movingVehiclesCount, totalStopsRemaining } = useDashboardStore();

  useEffect(() => {
    const handleTokenFromUrl = async () => {
      if (typeof window === 'undefined') return;
      if (tokenProcessedRef.current) return;

      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        setHasTokenInUrl(true);
        tokenProcessedRef.current = true;
        try {
          let isSuccess = false;
          const res = await Store.Client.Api.User.validateShareToken({ token });
          if (res.data.success && res.data.data) {
            const tokenData = res.data.data;
            if (tokenData.access_token) {
              Store.Client.setToken(tokenData.access_token);
              UserStore.set({
                token: tokenData.access_token,
                user: tokenData.publicInfo || tokenData.user || tokenData,
              });
              isSuccess = true;
            }
          }

          if (!isSuccess) {
            // If share token validation fails, try using the token directly as an access token
            Store.Client.setToken(token);
            const userRes = await Store.Client.Api.User.me();
            if (userRes.data.success && userRes.data.data) {
              UserStore.set({
                token,
                user: userRes.data.data,
              });
            }
          }
        } catch (err) {
          console.error('Error validating token:', err);
          tokenProcessedRef.current = false;
          // urlParams.delete('token');
          // const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
          // window.history.replaceState({}, '', newUrl);
        } finally {
          // Clear the token flag after processing (success or failure)
          setHasTokenInUrl(false);
        }
      }
    };

    handleTokenFromUrl();
  }, []);

  const deliveries = useMemo<DeliveryCardData[]>(() => {
    return orders.slice(0, 10).map((order) => {
      const rawData = order.rawData;
      const deliveryExecution = order.deliveryExecution;

      // Map delivery status
      let status: DeliveryCardData['status'] = 'IN_TRANSIT';
      if (order.deliveryStatus === 'DELIVERED' || order.deliveryStatus === 'COMPLETED') {
        status = 'DELIVERED';
      } else if (order.deliveryStatus === 'SENDING' || order.deliveryStatus === 'SENT' || order.deliveryStatus === 'DRIVING' || order.deliveryStatus === 'IN_TRANSIT') {
        status = 'IN_TRANSIT';
      }

      // Check if order has issues that might indicate missing/damaged items
      const documentNo = (order as { documentNo?: string }).documentNo;
      const orderIssues = issues.filter(issue => issue.orderId === order.id || issue.orderId === documentNo);
      const hasMissingIssue = orderIssues.some(issue => issue.type === 'missing');
      const hasDamagedIssue = orderIssues.some(issue => issue.type === 'damaged');

      if (hasDamagedIssue) {
        status = 'DAMAGED_ITEM';
      } else if (hasMissingIssue) {
        status = 'MISSING_ITEM';
      }

      // Get ETA or time
      let eta: string | undefined;
      let time: string | undefined;
      if (deliveryExecution?.plannedArrivalTime) {
        eta = moment(deliveryExecution.plannedArrivalTime).format('HH:mm');
      } else if (order.deliveryStatus === 'DELIVERED' || order.deliveryStatus === 'COMPLETED') {
        const completedTime = deliveryExecution?.factCompletionTimeGPS || deliveryExecution?.factCompletionTimeReported || rawData?.completedAt;
        if (completedTime) {
          time = moment(completedTime).format('HH:mm');
        }
      }

      return {
        id: order.id,
        orderNumber: rawData?.name || order.id,
        customer: rawData?.purchasingEntity?.company?.name || 'Unknown Customer',
        driver: deliveryExecution?.assignedDriverName || 'TBD',
        route: deliveryExecution?.runReference || 'N/A',
        depot: rawData?.purchasingEntity?.location?.name || 'N/A',
        zone: rawData?.shippingAddress?.province || rawData?.shippingAddress?.city || 'N/A',
        status,
        emoji: '📦',
        eta,
        time,
      };
    });
  }, [orders, issues]);

  const displayIssues = useMemo<IssueCardData[]>(() => {
    return issues.slice(0, 10).map((issue) => {
      const order = orders.find(o => o.id === issue.orderId || (o as { documentNo?: string }).documentNo === issue.orderId);
      const customerName = order?.rawData?.purchasingEntity?.company?.name || 'Unknown Customer';

      let status: IssueCardData['status'] = 'PENDING';
      if (issue.status === 'resolved') {
        status = 'RESOLVED';
      } else if (issue.status === 'in-progress') {
        status = 'IN_REVIEW';
      } else {
        status = 'PENDING';
      }

      const timeAgo = issue.createdAt
        ? moment(issue.createdAt).fromNow()
        : 'Unknown time';

      const channel: IssueCardData['channel'] = 'whatsapp';

      return {
        id: issue.id || issue.orderId,
        orderNumber: order?.rawData?.name || issue.orderId,
        customer: customerName,
        status,
        message: issue.description,
        channel,
        timeAgo,
        isPositive: issue.status === 'resolved' && issue.type === 'other',
      };
    });
  }, [issues, orders]);

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-GB', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      setLiveClock(timeString);
    };

    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!uiState.fetching && uiState.fetching !== undefined && !uiState.errorMes) {
      lastUpdateTimeRef.current = new Date();
    }
  }, [uiState.fetching, uiState.errorMes]);

  useEffect(() => {
    const updateLastUpdateText = () => {
      if (lastUpdateTimeRef.current) {
        const timeAgo = TimeHelper.formatTimeAgo(lastUpdateTimeRef.current);
        setLastUpdate(timeAgo);
      }
    };
    updateLastUpdateText();

    const interval = setInterval(updateLastUpdateText, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    const element = containerRef.current || document.documentElement;

    if (!isFullscreen) {
      // Enter fullscreen
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        (element as any).webkitRequestFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        (element as any).msRequestFullscreen();
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  };

  const handleIssueAction = (issueId: string, action: string) => {
    const url = `${window.location.origin}/order-issues/${issueId}`;
    window.open(url, '_blank');
  };

  const outForDelivery = useMemo(() => {
    return orders.filter(order => {
      const status = order.deliveryStatus;
      return status === 'SENDING' || status === 'SENT' || status === 'DRIVING' ||
        status === 'IN_TRANSIT' || status === 'ARRIVED' || status === 'IN_PROGRESS';
    }).length;
  }, [orders]);

  const delivered = useMemo(() => {
    return orders.filter(order => {
      return order.deliveryStatus === 'DELIVERED' || order.deliveryStatus === 'COMPLETED';
    }).length;
  }, [orders]);

  if (!isLoggedIn && !hasTokenInUrl) {
    return (
      <LoginForm
        title="Login Required"
        description="Please login to access the Operations Dashboard"
      />
    );
  }

  return (
    <Col ref={containerRef} style={[styles.container, { height: windowHeight + 2 }]}>
      <Header liveClock={liveClock} lastUpdate={lastUpdate} />
      <SummaryMetrics
        totalOrders={totalOrdersToday}
        outForDelivery={outForDelivery}
        delivered={delivered}
        issuesReported={totalOpenIssues}
      />
      <ScrollView style={{ flex: 1 }}>
        <Row padding={24} style={styles.content} alignItems="flex-start">
          <DeliveryFeed deliveries={deliveries} />
          <KPIPanel 
            snapshots={snapshots} 
            issuesByDriver={issuesByDriver}
            movingVehiclesCount={movingVehiclesCount}
            totalStopsRemaining={totalStopsRemaining}
          />
          <IssuesPanel issues={displayIssues} onIssueAction={handleIssueAction} />
        </Row>
      </ScrollView>
      <LiveTicker
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
      />
    </Col>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: LIGHT_GREY,
    overflow: 'hidden',
    width: '100%',
  },
  content: {
    // @ts-ignore
    gap: 32,
  },
});

OperationsDashboard.routeInfo = {
  title: 'MSC Operations Dashboard',
  path: '/operations/dashboard',
};

export default OperationsDashboard;
